import { DatePipe } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';

import { TwcPopupService, TwcValidators } from '@topcon/twc-angular';

import {
    ACL,
    AclControlService,
    JobSiteService,
    MagnetService,
    ProjectHelperService,
    ToastService,
    UiConfigurationService,
    notOnlyWhitespaces,
} from '@mirage-projects/cw-commons';
import { JobSite, Layer, Project, ProjectData, ProjectType } from '@mirage-projects/cw-models';

import { GridColumn } from '../../../models/grid-column';
import { KeyValue } from '../../../models/key-value';
import { CreateProjectContentService } from './create-project-content.service';

@Component({
    selector: 'cw-create-project-content',
    templateUrl: './create-project-content.component.html',
    styleUrls: ['./create-project-content.component.scss'],
    providers: [CreateProjectContentService],
    standalone: false,
})
export class CreateProjectContentComponent implements OnInit {
    /** Project type */
    @Input() type?: ProjectType | '' = ProjectType.COLLAGE_TYPE;

    /** Hide the create from layer option */
    @Input() shouldSkipLayers = true;

    /** Hide the create button */
    @Input() isCreateButtonHidden = false;

    /** Disable the ACL check when initializing the component */
    @Input() shouldSkipAclCheck = false;

    /** Prevent the navigation to project viewer */
    @Input() shouldOpenProjectViewer = true;

    /** The job site into which the created project will be added */
    @Input() jobSite: JobSite | null = null;

    /** Emit loading state */
    @Output() loadingChanged = new EventEmitter<boolean>();

    readonly columns: GridColumn[] = [
        {
            name: 'CREATE_PROJECT.LAYER_NAME',
            type: 'string',
            field: 'name',
        },
        {
            name: 'CREATE_PROJECT.LAYER_CRS',
            type: 'string',
            field: 'horizProjectionEPSG',
        },
    ];

    defaultMaxLength = 255;
    today = new Date();
    createProjectForm!: FormGroup;
    selectedLayers!: Layer[];
    ProjectTypeEnum = ProjectType;
    createProjectConfig = this.uiconfigurationService.uiConfiguration?.createProject;

    private loadingValue = false;

    constructor(
        private popupService: TwcPopupService,
        private router: Router,
        private jobSiteService: JobSiteService,
        private magnetService: MagnetService,
        private toastService: ToastService,
        private aclControlService: AclControlService,
        private uiconfigurationService: UiConfigurationService,
        private datePipe: DatePipe,
        private projectHelperService: ProjectHelperService,
        private createProjectContentService: CreateProjectContentService
    ) {}

    ngOnInit(): void {
        this.initCreateProjectForm();
        this.checkAcl();
        this.checkIsLayerSelected();
    }

    /**
     * Checks if the user has the permission to create a project
     */
    checkAcl(): void {
        if (this.shouldSkipAclCheck) {
            return;
        }

        if (!this.aclControlService.hasUserACL(ACL.TYPE_PROJECTS_CREATE)) {
            this.router.navigate(['/']);
        }
    }

    /**
     * Checks if a layer is selected and init the projection
     */
    checkIsLayerSelected(): void {
        this.selectedLayers = this.createProjectContentService.getCompleteSelectedLayers() ?? [];

        if (this.selectedLayers.length) {
            const projectionLayer = this.selectedLayers.find((layer: Layer) => layer.horizProjectionEPSG?.length);
            if (!projectionLayer) {
                return;
            }

            this.createProjectForm.patchValue({
                projection: projectionLayer.horizProjectionEPSG,
            });
        }
    }

    /**
     * Sets the selected coordinate reference system
     * @param value Selected CRS
     */
    setSelectedCRS(value: KeyValue): void {
        this.createProjectForm.patchValue({
            projection: `EPSG:${value?.key}`,
        });
    }

    /**
     * Sets the selected geoid
     * @param value Selected geoid
     */
    setSelectedGeoid(value: KeyValue): void {
        this.createProjectForm.patchValue({
            geoid: value?.key,
        });
    }

    /**
     * Gets the project data from the form
     */
    getProjectData(): ProjectData {
        const { name, description, projection, geoid, captureDate } = this.createProjectForm.getRawValue();

        const projectData: ProjectData = {
            name,
            description,
            geoid,
            horizProjectionEPSG: projection,
            captureDate: this.datePipe.transform(captureDate, 'YYYY-MM-dd') || undefined,
        };
        if (this.type === ProjectType.SCAN) {
            projectData.options = {
                propagatePermission: false,
            };
            projectData.projectType = this.type;
        }

        return projectData;
    }

    /**
     * Creates a new project
     */
    async onCreateProject(): Promise<void> {
        this.loading = true;
        const projectData: ProjectData = this.getProjectData();

        try {
            const project = await this.createProjectContentService.createProject(projectData, !this.shouldSkipLayers);
            const onRefresh$ = this.popupService.topPopup?.options?.data?.onRefresh$;
            if (this.jobSite) {
                this.jobSiteService.addProject(this.jobSite.id, project.id).subscribe(() => {
                    onRefresh$?.next({ projectId: project.id, projectType: this.type, openViewer: true });
                });
            }

            this.closePopup();

            if (this.shouldOpenProjectViewer) {
                this.openProjectViewer(project);
            }
        } catch (e) {
            this.toastService.error('CREATE_PROJECT.ERROR');
        } finally {
            this.loading = false;
        }
    }

    /**
     * Checks if the field is visible in the create project dialog
     * @param fieldKey Field key
     */
    isVisible(fieldKey: string): boolean {
        let isVisible = true;
        if (this.createProjectConfig) {
            const forbiddenFields = this.uiconfigurationService.getCreateProjectForbiddenFields(this.createProjectConfig, this.type ?? '');
            isVisible = !forbiddenFields?.some((field) => field.includes(fieldKey));
        }

        return isVisible;
    }

    /**
     * Closes the popup
     */
    closePopup(): void {
        const currentPopup = this.popupService.topPopup;
        this.popupService.hide(currentPopup);
    }

    /**
     * Opens the project viewer for the given project
     * @param project Project to open
     */
    openProjectViewer(project: Project): void {
        this.projectHelperService.openProjectViewer(project.id, project.projectType);
    }

    getProjection(): string {
        return this.jobSite?.horizProjectionEPSG ?? this.createProjectForm.get('projection')?.value ?? '';
    }

    get isSite(): boolean {
        return this.magnetService.isSite();
    }

    /**
     * Gets the max length for the project name
     */
    get nameMaxLength(): number {
        return this.isSite && this.type === ProjectType.SCAN ? 63 : this.defaultMaxLength;
    }

    /**
     * Gets the regex for the project name
     */
    get nameRegExp(): RegExp | string {
        return this.isSite && this.type === ProjectType.SCAN ? /^[^:*?!"#$%&',.;<=>@^`|~¥/\\ \p{Extended_Pictographic}]*$/u : '[\\s\\S]*';
    }

    /**
     * Gets the loading state
     */
    get loading(): boolean {
        return this.loadingValue;
    }
    /**
     * Sets the loading state
     * @param value Loading state
     */
    set loading(value: boolean) {
        this.loadingValue = value;
        this.loadingChanged.emit(value);
    }

    private initCreateProjectForm(): void {
        this.createProjectForm = new FormGroup({
            name: new FormControl('', [
                Validators.required,
                Validators.maxLength(this.nameMaxLength),
                Validators.pattern(this.nameRegExp),
                notOnlyWhitespaces(),
            ]),
            description: new FormControl(''),
            projection: new FormControl(''),
            geoid: new FormControl(''),
            captureDate: new FormControl(this.today, [TwcValidators.date, TwcValidators.maxDate(this.today)]),
        });
    }
}
